import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Phone, ChevronLeft, ChevronRight, X, CheckCircle2, Navigation, Image as ImageIcon, PlayCircle } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

import attractionsDataRaw from '../data/attractions.json';
const attractionsList = Array.isArray(attractionsDataRaw) ? attractionsDataRaw : (attractionsDataRaw?.default || []);

export default function AttractionDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'id';
  const navigate = useNavigate();
  
  const [attraction, setAttraction] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  // Referensi untuk mengontrol elemen video
  const mainVideoRef = useRef(null);
  const lightboxVideoRef = useRef(null);

  useEffect(() => {
    const data = attractionsList.find(a => a.slug === slug);
    setAttraction(data || null);
    setPhotoIdx(0); // Selalu mulai dari item pertama (Video)
    window.scrollTo(0, 0); 
  }, [slug]);

  // Efek untuk memutar ulang video dari detik 0 saat dipilih
  useEffect(() => {
    if (attraction && attraction.photos[photoIdx]?.type === 'video') {
      if (mainVideoRef.current) {
        mainVideoRef.current.currentTime = 0;
        mainVideoRef.current.play().catch(e => console.log("Autoplay di-block browser", e));
      }
      if (lightboxVideoRef.current && lightbox) {
        lightboxVideoRef.current.currentTime = 0;
        lightboxVideoRef.current.play().catch(e => console.log("Autoplay di-block browser", e));
      }
    }
  }, [photoIdx, attraction, lightbox]);

  const name = attraction ? (lang === 'zh' ? attraction.name_zh : (lang === 'en' ? attraction.name_en : attraction.name_id)) : 'Attraction Detail';
  const tagline = attraction ? (lang === 'zh' ? attraction.tagline_zh : (lang === 'en' ? attraction.tagline_en : attraction.tagline_id)) : '';
  const description = attraction ? (lang === 'zh' ? attraction.desc_zh : (lang === 'en' ? attraction.desc_en : attraction.desc_id)) : '';
  const activities = attraction ? (lang === 'zh' ? attraction.activities_zh : (lang === 'en' ? attraction.activities_en : attraction.activities_id)) : [];
  
  const distanceText = attraction 
    ? (lang === 'zh' ? `距离 Ndalem Mataram 约 ${attraction.distance} 公里` : (lang === 'en' ? `Around ${attraction.distance} km from Ndalem Mataram` : `Sekitar ${attraction.distance} km dari Ndalem Mataram`))
    : '';

  usePageTitle(name);

  if (!attraction) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6 pt-32">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="font-display text-2xl text-dark font-bold mb-2">
            {lang === 'id' ? 'Tempat pengalaman tidak ditemukan' : lang === 'zh' ? '未找到相关体验' : 'Experience not found'}
          </h2>
          <Link to="/attractions" className="text-primary-600 font-sans text-sm hover:underline">
            ← {lang === 'id' ? 'Kembali ke Pengalaman' : lang === 'zh' ? '返回体验列表' : 'Back to Experiences'}
          </Link>
        </div>
      </div>
    );
  }

  const photos = attraction.photos || [];
  const prev = () => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setPhotoIdx((i) => (i + 1) % photos.length);
  
  // Gambar Background Hero: Kita abaikan video dan ambil foto (image) pertama
  const heroBgImage = photos.find(p => p.type !== 'video')?.src || '/placeholder-image.jpg';

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      
      {/* ── HERO BANNER SECTION ── */}
      <div 
        className="relative pt-32 pb-40 px-6 min-h-[55vh] flex flex-col justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${heroBgImage}')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30"></div>
        
        <div className="relative max-w-6xl mx-auto w-full z-10">
          <button 
            onClick={() => navigate('/attractions')} 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white font-sans text-sm mb-6 transition-colors group bg-white/10 hover:bg-white/20 backdrop-blur-md px-4 py-2 rounded-full"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {lang === 'id' ? 'Kembali ke Pengalaman' : lang === 'zh' ? '返回体验列表' : 'Back to Experiences'}
          </button>
          
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold tracking-tight drop-shadow-md">
              {name}
            </h1>
            <p className="text-white/90 font-sans text-lg md:text-xl mt-4 leading-relaxed font-light drop-shadow">
              {tagline}
            </p>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-20 -mt-24">
        <div className="bg-white rounded-3xl shadow-xl shadow-black/5 p-6 md:p-8 lg:p-10 border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* KOLOM KIRI: GALERI FOTO/VIDEO */}
            <div className="lg:col-span-7 space-y-4">
              {photos.length > 0 && (
                <>
                  {/* Penampil Utama - DIUBAH MENJADI aspect-video */}
                  <div
                    className="group relative w-full aspect-video rounded-3xl overflow-hidden cursor-pointer bg-gray-100 shadow-md"
                    onClick={() => setLightbox(true)}
                  >
                    {photos[photoIdx].type === 'video' ? (
                      <video 
                        ref={mainVideoRef}
                        src={photos[photoIdx].src}
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img 
                        src={photos[photoIdx].src || '/placeholder-image.jpg'} 
                        alt={name} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                      />
                    )}
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80 pointer-events-none"></div>
                    
                    {photos.length > 1 && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); prev(); }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white backdrop-blur-md rounded-full flex items-center justify-center text-white hover:text-black transition-all shadow-lg opacity-0 group-hover:opacity-100">
                          <ChevronLeft size={20} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); next(); }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white backdrop-blur-md rounded-full flex items-center justify-center text-white hover:text-black transition-all shadow-lg opacity-0 group-hover:opacity-100">
                          <ChevronRight size={20} />
                        </button>
                      </>
                    )}
                    
                    <div className="absolute bottom-5 left-5 flex items-center gap-2 pointer-events-none">
                      {photos[photoIdx].type === 'video' ? (
                        <PlayCircle size={16} className="text-white/80" />
                      ) : (
                        <ImageIcon size={16} className="text-white/80" />
                      )}
                      <span className="text-white font-sans text-sm font-medium drop-shadow-md">
                        {lang === 'zh' ? photos[photoIdx]?.label_zh : (lang === 'en' ? photos[photoIdx]?.label_en : photos[photoIdx]?.label_id)}
                      </span>
                    </div>
                    
                    {photos.length > 1 && (
                      <div className="absolute bottom-5 right-5 bg-black/40 backdrop-blur-md text-white text-xs font-sans font-medium px-3 py-1.5 rounded-full shadow-md pointer-events-none">
                        {photoIdx + 1} / {photos.length}
                      </div>
                    )}
                  </div>
                  
                  {/* Thumbnail Scroll Horizontal */}
                  {photos.length > 1 && (
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x">
                      {photos.map((p, i) => (
                        <button 
                          key={i} 
                          onClick={() => setPhotoIdx(i)}
                          className={`relative h-20 w-28 sm:w-32 flex-shrink-0 rounded-2xl overflow-hidden bg-gray-100 transition-all snap-center ${
                            i === photoIdx 
                              ? 'ring-2 ring-primary-600 ring-offset-2 opacity-100' 
                              : 'opacity-60 hover:opacity-100'
                          }`}
                        >
                          {p.type === 'video' ? (
                            <div className="w-full h-full relative">
                              <video src={p.src} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <PlayCircle size={24} className="text-white" />
                              </div>
                            </div>
                          ) : (
                            <img src={p.src || '/placeholder-image.jpg'} alt="" className="w-full h-full object-cover" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* KOLOM KANAN: INFORMASI & DESKRIPSI */}
            <div className="lg:col-span-5 space-y-8">
              
              <div>
                <h2 className="font-display text-2xl text-slate-900 font-bold mb-4 flex items-center gap-2">
                  {lang === 'id' ? 'Tentang Pengalaman Ini' : lang === 'zh' ? '关于此体验' : 'About This Experience'}
                </h2>
                <p className="text-slate-600 font-sans text-[15px] leading-relaxed text-justify">
                  {description}
                </p>
              </div>
              
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-white shadow-sm rounded-full flex items-center justify-center flex-shrink-0 text-primary-600">
                    <Navigation size={18} />
                  </div>
                  <div>
                    <h3 className="font-sans font-bold text-slate-800 text-sm">
                      {lang === 'id' ? 'Jarak & Peta Lokasi' : lang === 'zh' ? '位置与地图' : 'Distance & Map'}
                    </h3>
                    <p className="text-slate-500 font-sans text-sm mt-0.5">
                      {distanceText}
                    </p>
                  </div>
                </div>
                
                {attraction.mapEmbed && (
                  <div className="w-full h-48 sm:h-56 rounded-xl overflow-hidden border border-slate-200 bg-slate-200 relative group">
                    <iframe
                      src={attraction.mapEmbed}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map of ${name}`}
                      className="absolute inset-0 grayscale group-hover:grayscale-0 transition-all duration-500"
                    ></iframe>
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="font-display font-bold text-slate-900 text-lg mb-4">
                  {lang === 'id' ? 'Aktivitas Populer' : lang === 'zh' ? '推荐热门体验' : 'Popular Activities'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activities.map((activity, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 shadow-sm p-3.5 rounded-xl hover:border-primary-200 transition-colors">
                      <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm font-sans font-medium text-slate-700">
                        {activity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-2 border-t border-slate-100">
                <a 
                  href={`https://wa.me/6281378575758?text=${encodeURIComponent(
                    lang === 'id' ? `Halo Ndalem Mataram, saya ingin bertanya info atau pemesanan tur/pengalaman ke ${name}.` : 
                    lang === 'zh' ? `你好 Ndalem Mataram，我想进一步咨询有关前往 ${name} 的体验或包车旅游。` : 
                    `Hello Ndalem Mataram, I would like to ask further about tour/experience info to ${name}.`
                  )}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] hover:bg-[#1EBE5A] text-white font-sans font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-[#25D366]/30 hover:shadow-[#25D366]/50 hover:-translate-y-0.5"
                >
                  <Phone size={18} className="animate-pulse" />
                  {lang === 'id' ? 'Tanya Paket via WhatsApp' : lang === 'zh' ? '通过 WhatsApp 咨询' : 'Inquire via WhatsApp'}
                </a>
              </div>

            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/attractions" className="inline-flex items-center gap-2 text-slate-500 hover:text-primary-600 font-sans text-sm font-medium transition-colors group">
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {lang === 'id' ? 'Lihat Semua Pengalaman Lainnya' : lang === 'zh' ? '查看所有体验' : 'Explore Other Experiences'}
          </Link>
        </div>
      </div>

      {/* ── LIGHTBOX LAYAR PENUH ── */}
      {lightbox && photos[photoIdx] && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 lg:p-10 backdrop-blur-sm" onClick={() => setLightbox(false)}>
          <button className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-50">
            <X size={24} />
          </button>
          
          {/* DIUBAH MENJADI aspect-video DI SINI */}
          <div className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden bg-black" onClick={(e) => e.stopPropagation()}>
            {photos[photoIdx].type === 'video' ? (
              <video 
                ref={lightboxVideoRef}
                src={photos[photoIdx].src}
                controls={true}
                autoPlay
                loop
                playsInline
                className="w-full h-full object-cover" 
              />
            ) : (
              <img src={photos[photoIdx].src || '/placeholder-image.jpg'} alt="" className="w-full h-full object-cover" /> 
            )}
            
            {photos.length > 1 && (
              <>
                <button onClick={prev} className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 bg-black/50 hover:bg-black/80 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all border border-white/10">
                  <ChevronLeft size={28} />
                </button>
                <button onClick={next} className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 bg-black/50 hover:bg-black/80 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all border border-white/10">
                  <ChevronRight size={28} />
                </button>
              </>
            )}
            
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 border border-white/10 text-white text-sm font-sans font-medium px-5 py-2.5 rounded-2xl backdrop-blur-md text-center max-w-[90%] truncate pointer-events-none">
               {lang === 'zh' ? photos[photoIdx]?.label_zh : (lang === 'en' ? photos[photoIdx]?.label_en : photos[photoIdx]?.label_id)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}